<template>
  <ApplicationLayout>
    <v-container
      fluid
      px-12
      py-6
      v-if="ready"
    >
      <div
        class="
          d-flex
          align-center
        "
      >
        <BackTitle
          :to="{
            name: 'brands',
            query: { focus: brand.id }
          }"
          :title="title"
          :subtitle="brandName"
        ></BackTitle>

        <v-spacer></v-spacer>

        <v-btn
          color="primary"
          depressed
          @click="goToFront(`nos-marques/${brand.uri}`)"
        >
          <v-icon left>input</v-icon>
          View brand
        </v-btn>

        <v-btn
          color="primary"
          depressed
          @click="goToFront(`bons-plans/${brand.uri}`)"
          class="ml-3"
        >
          <v-icon left>input</v-icon>
          View deals
        </v-btn>
      </div>

      <v-card
        class="pa-6 mt-6"
      >
        <v-row>
          <v-col
            cols="6"
          >
            <v-form
              @submit.prevent="submit()"
              ref="form"
              v-model="valid"
            >
              <v-text-field
                v-model="brand.name"
                label="Name"
                :rules="rules.name"
              ></v-text-field>

              <TextFieldImage
                label="Image"
                v-model="brand.image"
                name="brandImage"
              ></TextFieldImage>

              <v-select
                v-model="brand.status"
                :items="statuses"
                label="Status"
              ></v-select>

              <v-text-field
                readonly
                :value="
                  ebuyclubPartner.name
                  ? `${ebuyclubPartner.name} (${ebuyclubPartner.id})`
                  : null
                "
                label="eBuyClub Partner"
              >
                <template slot="append-outer">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        text
                        icon
                        small
                        v-on="on"
                        @click="partner.dialog = true"
                      >
                        <v-icon>menu_open</v-icon>
                      </v-btn>
                    </template>
                    <span>Select eBuyClub Partner</span>
                  </v-tooltip>
                </template>
              </v-text-field>

              <TextFieldImage
                label="Banner"
                v-model="brand.banner"
                name="brandBanner"
              ></TextFieldImage>

              <TextFieldImage
                label="Banner small"
                v-model="brand.banner_small"
                name="brandBannerSmall"
              ></TextFieldImage>

              <v-textarea
                v-model="brand.about"
                label="About (eBuyClub description will be used by default)"
              ></v-textarea>

              <v-row
                justify="end"
              >
                <v-btn
                  :disabled="!modified"
                  v-if="editMode"
                  class="mr-4"
                  @click="cancel()"
                  depressed
                >Cancel</v-btn>

                <v-btn
                  :disabled="!valid || !modified"
                  color="primary"
                  type="submit"
                  class="mr-4"
                  depressed
                >Save</v-btn>

                <v-btn
                  :disabled="!valid || !modified"
                  color="primary"
                  @click="saveAndGoBack()"
                  depressed
                >Save & Go back</v-btn>
              </v-row>
            </v-form>
          </v-col>

          <v-col
            cols="6"
          >
            <v-container
              fluid
              pa-0
              fill-height
            >
              <v-row
                align="center"
                justify="center"
              >
                <img
                  v-if="brand.image"
                  :src="brand.image"
                  :alt="'image - ' + brand.title"
                  style="max-width: 70%"
                />
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </v-card>

      <ProductsList
        v-if="brand.id"
        class="mt-12"
        title="Brand products"
        :scope="{ brandId: brand.id }"
      ></ProductsList>

      <v-dialog
        v-model="partner.dialog"
        max-width="450"
        persistent
      >
        <v-card>
          <v-card-title
            class="headline"
          >Select eBuyClub Partner</v-card-title>

          <v-card-text>
            <v-autocomplete
              v-model="partner.model"
              :loading="partner.loading"
              :items="partner.items"
              :search-input.sync="partnerSearch"
              cache-items
              hide-no-data
              clearable
              placeholder="Start typing to search..."
              prepend-inner-icon="search"
            ></v-autocomplete>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn
              color="primary"
              text
              @click="partner.dialog = false"
            >Cancel</v-btn>

            <v-btn
              color="primary"
              @click="selectEbuyclubPartner()"
              depressed
            >Validate</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </ApplicationLayout>
</template>

<script>
import { mapState } from 'vuex'
import ApplicationLayout from '../components/layouts/ApplicationLayout.vue'
import BackTitle from '../components/BackTitle.vue'
import ProductsList from '../components/ProductsList.vue'
import TextFieldImage from '../components/TextFieldImage.vue'

export default {
  components: {
    ApplicationLayout,
    BackTitle,
    ProductsList,
    TextFieldImage
  },
  data() {
    return {
      valid: false,
      ready: false,
      brandCopy: null,
      partnerCopy: null,
      partner: {
        dialog: false,
        model: null,
        loading: false,
        results: [],
        items: []
      },
      partnerSearch: null,
      editMode: !!this.$route.params.id,
      statuses: [
        {
          value: 'hidden',
          text: this.$translate.instant('BRAND_HIDDEN')
        },
        {
          value: 'displayed',
          text: this.$translate.instant('BRAND_DISPLAYED')
        }
      ],
      rules: {
        name: [v => !!v || 'Name is required']
      }
    }
  },
  computed: {
    ...mapState('Brands', [
      'brand',
      'brandName',
      'ebuyclubPartner'
    ]),
    title() {
      return this.editMode ? 'Brand' : 'Create new brand'
    },
    modified() {
      return this.brandCopy !== JSON.stringify(this.brand)
        || this.partnerCopy !== JSON.stringify(this.ebuyclubPartner)
    }
  },
  async created() {
    await this.getBrand(this.$route.params.id)
    this.getEbuyclubPartner(this.$route.params.id)
    this.ready = true
  },
  watch: {
    async partnerSearch(val) {
      if (
        !val
        || val.length < 3
        || this.partner.items.some(({ text }) => text === val)
      ) return

      this.partner.loading = true
      try {
        const response = await this.$axios.get(`/ebuyclub-partners?name=${val.toLowerCase().trim()}`)
        this.partner.results = response.data.data
        this.partner.items = response.data.data.map(({ name, id }) => ({ text: name, value: id }))
      } catch (error) {
        this.partner.items = []
      } finally {
        this.partner.loading = false
      }
    }
  },
  methods: {
    async getBrand(id) {
      await this.$store.dispatch('Brands/get', id)
      this.brandCopy = JSON.stringify(this.brand)
    },
    async getEbuyclubPartner(id) {
      const partner = await this.$store.dispatch('Brands/getEbuyclubPartner', id)
      if (partner.id) {
        this.partner.items = [{ text: partner.name, value: partner.id }]
        this.partner.model = partner.id
      }
      this.partnerCopy = JSON.stringify(this.ebuyclubPartner)
    },
    cancel() {
      this.$store.commit('Brands/setBrand', JSON.parse(this.brandCopy))
      this.$store.commit('Brands/setEbuyclubPartner', JSON.parse(this.partnerCopy))
    },
    async submit() {
      if (!this.$refs.form.validate()) return
      const snackbar = { text: 'Brand update succeed' }
      try {
        await this.$store.dispatch('Brands/createOrUpdate')
        if (!this.editMode) {
          this.$router.push({ name: 'brand', params: { id: this.brand.id } })
          snackbar.text = 'Brand creation succeed'
          this.editMode = true
        }
        this.brandCopy = JSON.stringify(this.brand)
      } catch (error) {
        snackbar.text = this.editMode ? 'Brand update failed' : 'Brand creation failed'
        snackbar.color = 'error'
      }
      this.$store.commit('Global/setSnackbar', snackbar)
    },
    async saveAndGoBack() {
      await this.submit()
      this.$router.push({ name: 'brands' })
    },
    selectEbuyclubPartner() {
      if (this.partner.model !== this.brand.ebuyclub_partner_id) {
        const partner = this.partner.results.find(({ id }) => id === this.partner.model)
        this.$store.commit('Brands/setEbuyclubPartner', partner)
        this.brand.ebuyclub_partner_id = this.partner.model || null
      }
      this.partner.dialog = false
    },
    goToFront(uri) {
      window.open(`${process.env.VUE_APP_FRONT_URL}${uri}.html`, '_blank')
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.name === from.name && to.params.id !== from.params.id) {
      await this.getBrand(to.params.id)
      this.getEbuyclubPartner(to.params.id)
    }
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (from.name !== 'new-brand') {
      this.$store.commit('Brands/setBrand', null)
      this.$store.commit('Brands/setEbuyclubPartner', null)
      this.$store.commit('Products/setProducts', null)
    }
    next()
  }
}
</script>
